<template>
	<AppDialog :dialogName="dialogName" width="620">
		<div class="create-sync-group">
			<div class="pt-8">
				<div class="title">
					New synced group -
					<IntegrationIcon
						class="integration-icon"
						:integrationName="service.service_slug"
						:size="serviceIconWidth"
						:padding="serviceIconPadding"
					/>
					{{ serviceName }}
				</div>
			</div>
			<div class="my-4 px-4 group-body">
				<GroupUsePermission @selected-use-permission-type="onUsePermissionChange" :group="group" />
				<GroupPermittedUses @selected-permitted-use="onPermittedUseChange" :group="group" />
			</div>
			<div class="button flex-centerize pt-2">
				<PrimaryButton class="btn create-btn" :disabled="isSaveDisabled || localDisableSave" @click="updateOrCreateGroup">{{
					btnText
				}}</PrimaryButton>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import AppDialog from '../AppDialog/AppDialog';
import AppDialogManagerService from '../AppDialogManager/app-dialog-manager-service';
import { inviteToAccount } from '@/store/modules/account/actions';
import { createGroup, updateGroup } from '@/store/modules/accountGroups/actions';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import GroupsService from '@/utils/groups-service';
import { pluralize } from '@/utils/language-service';
import GroupPermittedUses from '@/views/settings/groups/tabs/SyncedGroupPermissions/GroupPermittedUses';
import GroupUsePermission from '@/views/settings/groups/tabs/SyncedGroupPermissions/GroupUsePermission';

export default {
	name: 'GroupPermissionsDialog',
	components: {
		AppDialog,
		GroupUsePermission,
		GroupPermittedUses,
	},
	props: {
		service: { type: Object, required: true },
		profileId: { type: [Number, String], required: true },
		isUpdate: { type: Boolean, default: false },
		group: { type: Object, default: null },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters([getAccountConnection.fullPath]),
		btnText() {
			return this.isUpdate ? 'Update group' : 'Create group';
		},
		serviceName() {
			return this.service?.service_name || 'Service';
		},
		serviceGroupNoun() {
			return GroupsService.serviceGroupNoun(this.service);
		},
		usersAndGroupsCountText() {
			return `${pluralize('users', this.userMatchingUsers.length, true)} and ${pluralize(
				this.serviceGroupNoun,
				this.userMatchingGroups.length,
				true
			)}`;
		},
		subGroupsText() {
			return `${this.serviceName} ${pluralize(this.serviceGroupNoun, 2)}`;
		},
		isSaveDisabled() {
			return !this.didUsePermissionChange && !this.didPermittedUseChange;
		},
		didUsePermissionChange() {
			return this.usePermissionType !== this.group.access_type;
		},
		didPermittedUseChange() {
			return Object.keys(this.permittedUse).some((key) => this.permittedUse[key] != this.group.usage_preferences[key]);
		},
	},
	methods: {
		...mapActions([createGroup.fullPath, updateGroup.fullPath, inviteToAccount.fullPath]),
		onUsePermissionChange(newV) {
			this.usePermissionType = newV;
		},
		onPermittedUseChange(newV) {
			this.permittedUse = newV;
		},
		async updateOrCreateGroup() {
			if (!this.isSaveDisabled) {
				this.localDisableSave = true;
				const group = {
					...this.group,
					access_type: this.usePermissionType,
					usage_preferences: this.permittedUse,
				};
				try {
					if (this.isUpdate) {
						await this[updateGroup.fullPath](group);
						this.dialogPromiseHandlers.resolve('updated');
					} else {
						const res = await this[createGroup.fullPath](group);
						this.dialogPromiseHandlers.resolve({ group: res, status: 'created' });
					}
					this.$toast.success(`Group ${this.isUpdate ? 'updated' : 'created'}`);
				} catch {
					this.$toast.error(`Failed ${this.isUpdate ? 'updating' : 'creating'} group`);
				} finally {
					AppDialogManagerService.closeCurrentDialog();
					this.localDisableSave = false;
				}
			}
		},
	},
	async mounted() {
		if (this.isUpdate && this.group) {
			this.usePermissionType = this.group.access_type;
			this.permittedUse = { ...this.group.usage_preferences };
		}
	},
	data() {
		return {
			loading: false,
			serviceIconWidth: 30,
			serviceIconPadding: 10,
			dialogName: 'GroupPermissionsDialog',
			usePermissionType: undefined,
			permittedUse: {},
			localDisableSave: false,
		};
	},
};
</script>

<style scoped lang="scss">
.create-sync-group {
	height: 500px;
	.title {
		font-weight: 600;
		font-size: 16px;
		display: flex;
		justify-content: flex-start;
		padding-left: 30px;
		.integration-icon {
			padding: 0px 10px;
		}
	}
	.synced-group-users-action-type {
		padding: 0px 30px;
		::v-deep {
			.v-label {
				font-size: small;
				color: var(--primary-font-color) !important;
			}
		}
	}
	.users-and-groups {
		max-height: 160px;
		overflow: auto;
		background: #f0ebeb73;
		border-radius: 9px;
	}
	.options {
		height: 190px;
		overflow: auto;
		&.tall {
			height: 310px;
		}
	}
	.see-details {
		text-decoration: underline;
		cursor: pointer;
	}
}
</style>
